/**
 * Login loop detection and storage probing.
 *
 * The OAuth flow can succeed completely — authorize 303s, code exchanges for a
 * token, userinfo returns the user — and still leave the app unauthenticated on
 * the next page load, if the browser does not retain what we wrote to
 * localStorage (privacy/incognito modes, managed profiles, extensions or
 * security proxies that clear site data per navigation). The app then redirects
 * to login, the auth session cookie is still valid so a fresh code comes
 * straight back, and the user is trapped bouncing between hub and auth with a
 * 200 at every layer and no error anywhere.
 *
 * State here lives in a COOKIE rather than sessionStorage on purpose: in this
 * failure mode cookies are demonstrably still retained (that is precisely why
 * the auth server keeps auto-approving), so the guard keeps working when Web
 * Storage is the thing that is broken.
 */

const ATTEMPTS_COOKIE = 'solid_login_attempts';
const PROBE_KEY = 'solid-storage-probe';

/** Redirects allowed inside the window before we call it a loop. */
const MAX_REDIRECTS = 3;
/** Attempts older than this are treated as an unrelated, earlier session. */
const WINDOW_MS = 60_000;

interface AttemptState {
  /** Number of login redirects issued in the current window. */
  n: number;
  /** Epoch ms of the first redirect in the current window. */
  first: number;
}

function readCookie(name: string): string | null {
  for (const cookie of document.cookie.split(';')) {
    const [key, ...rest] = cookie.trim().split('=');
    if (key === name) return decodeURIComponent(rest.join('='));
  }
  return null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

function readAttempts(): AttemptState {
  const raw = readCookie(ATTEMPTS_COOKIE);
  if (!raw) return { n: 0, first: 0 };
  try {
    const parsed = JSON.parse(raw) as AttemptState;
    if (typeof parsed?.n !== 'number' || typeof parsed?.first !== 'number') {
      return { n: 0, first: 0 };
    }
    // Outside the window this is a stale count from an earlier visit.
    if (Date.now() - parsed.first > WINDOW_MS) return { n: 0, first: 0 };
    return parsed;
  } catch {
    return { n: 0, first: 0 };
  }
}

/**
 * Record that we are about to send the user to the login page.
 * Returns the number of redirects issued within the current window.
 */
export function recordLoginRedirect(): number {
  const current = readAttempts();
  const next: AttemptState = {
    n: current.n + 1,
    first: current.first || Date.now(),
  };
  writeCookie(ATTEMPTS_COOKIE, JSON.stringify(next), Math.ceil(WINDOW_MS / 1000));
  return next.n;
}

/** True once redirects have repeated enough to be a loop rather than a retry. */
export function isLoginLooping(): boolean {
  return readAttempts().n >= MAX_REDIRECTS;
}

/** Clear the counter — call as soon as the user is actually authenticated. */
export function clearLoginRedirects(): void {
  writeCookie(ATTEMPTS_COOKIE, '', 0);
}

/**
 * Whether localStorage accepts a write and returns it again.
 *
 * Catches storage that is unavailable or silently dropping writes. It cannot
 * detect storage that is cleared later (on navigation), which is what the
 * redirect counter above is for — the two together cover both shapes.
 */
/**
 * What storage looked like at page load, sampled before any app code can
 * change it. Captured eagerly at module scope because by the time a loop is
 * detected the interesting state (was a token present when we arrived?) has
 * already been overwritten.
 */
const arrivalState = {
  hadStoredToken: (() => {
    try {
      return !!(localStorage.getItem('solid-hub-auth') || localStorage.getItem('solid-auth'));
    } catch {
      return false;
    }
  })(),
  hadSharedCookie: document.cookie.includes('solid_auth_token='),
  cookiesEnabled: navigator.cookieEnabled,
};

/**
 * Report a detected loop to the API, anonymously.
 *
 * The people hitting this are customers, not engineers, so the app has to
 * produce its own diagnosis rather than relying on anyone inspecting a
 * browser. Sends booleans only — no identifiers, no token material. Failure
 * to report is ignored: this runs while the app is already broken and must
 * never make that worse.
 */
export function reportLoginLoop(attempts: number): void {
  try {
    const body = JSON.stringify({
      attempts,
      storageWritable: storageAcceptsWrites(),
      hadStoredToken: arrivalState.hadStoredToken,
      hadSharedCookie: arrivalState.hadSharedCookie,
      cookiesEnabled: arrivalState.cookiesEnabled,
    });
    // keepalive so the report survives the navigation that usually follows.
    fetch('/api/v1/auth/diagnostics/login-loop', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never let diagnostics break an already-failing page.
  }
}

export function storageAcceptsWrites(): boolean {
  try {
    const probe = String(Date.now());
    localStorage.setItem(PROBE_KEY, probe);
    const readBack = localStorage.getItem(PROBE_KEY);
    localStorage.removeItem(PROBE_KEY);
    return readBack === probe;
  } catch {
    return false;
  }
}
